import React, { Suspense, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { MenuItem } from '@/types/menu';
import { MenuItemCard } from './MenuItemCard';
import { MenuItemsSkeleton } from './MenuItemsSkeleton';
import { EmptyMenuItems } from './EmptyMenuItems';

interface VirtualizedMenuItemsListProps {
  items: MenuItem[];
  isLoading?: boolean;
  searchTerm?: string;
  categoryId: string;
  onEditItem?: (item: MenuItem) => void;
  onDeleteItem?: (id: string, name: string) => Promise<void>;
  onToggleAvailability?: (id: string, available: boolean) => Promise<void>;
}

const ITEM_HEIGHT = 200; // Adjust based on your MenuItemCard height
const ITEMS_PER_ROW = 4; // Number of items per row

const VirtualizedMenuItemsList = React.memo<VirtualizedMenuItemsListProps>(({
  items,
  isLoading,
  searchTerm,
  categoryId,
  onEditItem,
  onDeleteItem,
  onToggleAvailability,
}) => {
  // Group items into rows for grid layout
  const rows = useMemo(() => {
    const rows: MenuItem[][] = [];
    for (let i = 0; i < items.length; i += ITEMS_PER_ROW) {
      rows.push(items.slice(i, i + ITEMS_PER_ROW));
    }
    return rows;
  }, [items]);

  if (isLoading) {
    return <MenuItemsSkeleton />;
  }

  if (!items || items.length === 0) {
    const message = searchTerm 
      ? `No menu items found matching "${searchTerm}"`
      : undefined;
    return <EmptyMenuItems message={message} />;
  }

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const rowItems = rows[index];
    
    return (
      <div style={style} className="flex gap-3 md:gap-4">
        {rowItems.map((item, itemIndex) => (
          <div key={`${categoryId}-${item.id}`} className="flex-1">
            <MenuItemCard
              item={item}
              onEdit={onEditItem || (() => {})}
              onDelete={onDeleteItem || (() => Promise.resolve())}
              onToggleAvailability={onToggleAvailability || (() => Promise.resolve())}
            />
          </div>
        ))}
        {/* Fill empty spaces in the last row */}
        {rowItems.length < ITEMS_PER_ROW && 
          Array.from({ length: ITEMS_PER_ROW - rowItems.length }).map((_, i) => (
            <div key={`empty-${index}-${i}`} className="flex-1" />
          ))
        }
      </div>
    );
  };

  return (
    <div className="h-full w-full">
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            itemCount={rows.length}
            itemSize={ITEM_HEIGHT}
            width={width}
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          >
            {Row}
          </List>
        )}
      </AutoSizer>
    </div>
  );
});

VirtualizedMenuItemsList.displayName = 'VirtualizedMenuItemsList';

// Wrapper component with Suspense
export const VirtualizedMenuItemsListWithSuspense = (props: VirtualizedMenuItemsListProps) => {
  return (
    <Suspense fallback={<MenuItemsSkeleton />}>
      <VirtualizedMenuItemsList {...props} />
    </Suspense>
  );
}; 